import React, { useEffect, useMemo, useState } from "react";
import { useApiCall } from "../custom-hooks/api-call";

/* =========================================================================
   Legendesk Sales — Management (exec overview: KPIs, pod ranking, trend chart)
   ========================================================================= */

// ---- palette -------------------------------------------------------------
const C = {
  linen: "#F7ECDF", linenDeep: "#EFE1CF", ink: "#0A0C12", ink70: "#3A3D47",
  ink50: "#6B6E78", ink30: "#9A9CA4", card: "#FFFFFF", line: "#E7DAC6",
  lineSoft: "#F0E6D6", indigo: "#4F6EF7", indigoSoft: "#EAEEFF",
  green: "#1F9D6B", greenSoft: "#E4F5EC", amber: "#C9871A", amberSoft: "#FBEFD6",
  red: "#D0503C", redSoft: "#FBE6E0",
};
const FONT_DISP = "'Sora', system-ui, sans-serif";
const FONT_BODY = "'DM Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

// ---- rng -----------------------------------------------------------------
function mulberry32(a) { return function () { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
function hash(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
function gauss(r) { return (r() + r() + r()) / 3; }

const FIRST = ["Ayesha", "Bilal", "Zara", "Hamza", "Sana", "Usman", "Mariam", "Faisal", "Noor", "Ahmed", "Hira", "Talha", "Iqra", "Danish", "Fatima", "Saad", "Amna", "Rehan", "Kiran", "Owais", "Mehak", "Zain", "Rida", "Adeel", "Anaya", "Kashif", "Laiba", "Waleed", "Sadia", "Umar", "Aiman", "Junaid", "Sania", "Haris", "Areeba", "Fahad", "Nimra", "Shahzad", "Warda", "Bilawal", "Emma", "Liam", "Sophia", "Noah", "Olivia", "Ethan", "Mia", "Lucas", "Ella", "Mason"];
const LAST = ["Khan", "Malik", "Sheikh", "Raza", "Qureshi", "Butt", "Ansari", "Hashmi", "Siddiqui", "Farooq", "Chaudhry", "Baig", "Awan", "Mirza", "Rana", "Shah", "Gill", "Dar", "Jamil", "Nawaz", "Rivera", "Chen", "Patel", "Nguyen", "Brooks", "Silva"];
const TEAMS = ["Apex", "Titan", "Falcon", "Orbit", "Vanguard"];

function buildAgents(count = 100) {
  const rng = mulberry32(20260703); const out = []; const used = new Set();
  for (let i = 0; i < count; i++) {
    let name; do { name = FIRST[Math.floor(rng() * FIRST.length)] + " " + LAST[Math.floor(rng() * LAST.length)]; } while (used.has(name)); used.add(name);
    const skill = 0.55 + gauss(rng) * 0.85; const vol = 0.05 + rng() * 0.22;
    const baseTarget = 9000 + Math.round(rng() * 17000);
    const refundBase = rng() < 0.16 ? 0.14 + rng() * 0.16 : 0.02 + rng() * 0.09;
    const history = []; let drift = (rng() - 0.5) * 0.06;
    for (let p = 0; p < 6; p++)history.push(Math.max(0.15, skill + drift * (p - 2.5) + (gauss(rng) - 0.5) * vol * 2));
    out.push({ id: "AG" + String(i + 1).padStart(3, "0"), name, team: TEAMS[i % TEAMS.length], skill, vol, baseTarget, refundBase, history });
  }
  return out;
}
const AGENTS = buildAgents(100);

const PERIODS = [
  { key: "today", label: "Today", factor: 1 / 22 }, { key: "yesterday", label: "Yesterday", factor: 1 / 22 },
  { key: "this_week", label: "This week", factor: 1 / 4.3 }, { key: "this_month", label: "This month", factor: 1 },
  { key: "last_30_days", label: "Last 30 days", factor: 1 }, { key: "last_month", label: "Last month", factor: 0.96 },
  { key: "rolling_quarter", label: "Rolling quarter", factor: 3 }, { key: "this_quarter", label: "This quarter", factor: 2.1 },
  { key: "last_quarter", label: "Last quarter", factor: 3 }, { key: "this_year", label: "This year", factor: 6.4 },
  { key: "all_time", label: "All time", factor: 27 },
  { key: "custom", label: "Custom" },
];

function computeRow(agent, period) {
  const rng = mulberry32(hash(agent.id + "|" + period.key));
  const attain = Math.max(0.12, agent.skill + (gauss(rng) - 0.5) * agent.vol * 2.2);
  const target = Math.round(agent.baseTarget * period.factor);
  const gross = Math.round(target * attain);
  const refundRatio = Math.max(0.005, agent.refundBase + (gauss(rng) - 0.5) * 0.05);
  const refunds = Math.round(gross * refundRatio); const net = gross - refunds;
  return { ...agent, target, gross, net, refunds, refundRatio, progress: target > 0 ? net / target : 0 };
}
function statusOf(p) { if (p >= 1) return "on_target"; if (p >= 0.9) return "on_track"; if (p >= 0.7) return "at_risk"; return "behind"; }
const STATUS_META={
  on_target:{label:"On target",c:C.green,bg:C.greenSoft},
  on_track:{label:"On track",c:C.indigo,bg:C.indigoSoft},
  at_risk:{label:"At risk",c:C.amber,bg:C.amberSoft},
  behind:{label:"Behind",c:C.red,bg:C.redSoft},
  unassigned:{ label: "Unassigned", c: C.gray, bg: C.graySoft}
};
function trendOf(h) { const avg = h.reduce((a, b) => a + b, 0) / h.length; const std = Math.sqrt(h.reduce((a, b) => a + (b - avg) ** 2, 0) / h.length); const s = h[h.length - 1] - h[0]; if (std < 0.12 && avg >= 0.95) return "consistent"; if (s > 0.18) return "improving"; if (s < -0.18) return "declining"; if (std >= 0.22) return "volatile"; return "steady"; }

function money(n) { const a = Math.abs(n); const s = n < 0 ? "-" : ""; if (a >= 1e6) return s + "$" + (a / 1e6).toFixed(a >= 1e7 ? 1 : 2) + "M"; if (a >= 1e3) return s + "$" + (a / 1e3).toFixed(a >= 1e4 ? 0 : 1) + "K"; return s + "$" + Math.round(a); }
const pct = (x) => Math.round(x * 100) + "%";
const initials = (n) => { const p = n.split(" "); return (p[0][0] + (p[1] ? p[1][0] : "")).toUpperCase(); };

const PRIOR_PERIOD = { today: "yesterday", this_month: "last_month", this_q: "last_q" };

// ---- unit/pod colors — assigned deterministically by name since the set of
// units is dynamic (comes from the backend, not a fixed roster) -----------
const UNIT_PALETTE = [C.indigo, C.green, C.amber, C.red, "#8B5CF6", "#0EA5E9", "#EC4899", "#14B8A6", "#F97316", "#64748B"];
function colorForUnit(name) { return UNIT_PALETTE[hash(name || "") % UNIT_PALETTE.length]; }

// ---- shared components ---------------------------------------------------
function ProgressBar({ value, color }) {
  const capped = Math.min(value, 1.25);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ position: "relative", flex: 1, height: 7, background: C.lineSoft, borderRadius: 99, overflow: "hidden" }}>
        <div style={{ position: "absolute", left: `${(1 / 1.25) * 100}%`, top: 0, bottom: 0, width: 1, background: C.ink30 }} />
        <div style={{ height: "100%", width: `${(capped / 1.25) * 100}%`, background: color, borderRadius: 99, transition: "width .35s ease" }} />
      </div>
      <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: C.ink, minWidth: 40, textAlign: "right" }}>{pct(value)}</span>
    </div>
  );
}
function Pill({ label, color, bg }) {
  return (<span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 99, background: bg, color, fontSize: 11.5, fontWeight: 600 }}><span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />{label}</span>);
}
function ProgressRing({ value, color, size = 132, stroke = 12 }) {
  const capped = Math.min(Math.max(value, 0), 1);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - capped);
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.lineSoft} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset .4s ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: FONT_DISP, fontWeight: 700, fontSize: 22, color: C.ink }}>{pct(value)}</div>
        <div style={{ fontSize: 10, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>of target</div>
      </div>
    </div>
  );
}
function TrendChart({ categories, series, height = 280, yFormatter = money }) {
  const [gradId] = useState(() => "tcg" + Math.random().toString(36).slice(2, 9));
  // width is close to the real rendered column width so the viewBox scale factor stays ~1 —
  // otherwise the whole coordinate system (incl. font-size/stroke-width) gets magnified when
  // the browser stretches a much-narrower intrinsic width up to fill a wide card.
  const width = 960, padL = 58, padR = 18, padT = 18, padB = 30;
  const innerW = width - padL - padR, innerH = height - padT - padB;
  const allVals = series.flatMap(s => s.values);
  const maxV = Math.max(...allVals, 0.0001);
  const minV = Math.min(0, ...allVals);
  const span = (maxV - minV) || 1;
  const xFor = (i) => categories.length > 1 ? padL + (i / (categories.length - 1)) * innerW : padL + innerW / 2;
  const yFor = (v) => padT + innerH - ((v - minV) / span) * innerH;
  const ticks = 4;
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) => minV + (span * i / ticks));
  const showArea = series.length === 1;
  const dense = categories.length > 12; // hourly/daily views: thin out labels & dots so they don't overlap
  const labelStep = dense ? Math.ceil(categories.length / 9) : 1;
  // many series (e.g. one line per unit) -> shrink legend chips & line/dot weight so the chart doesn't turn to mush
  const manySeries = series.length > 5;
  const lineW = manySeries ? 1.5 : 2.4;
  const dashedLineW = manySeries ? 1.2 : 1.8;
  const dotR = manySeries ? 2.4 : 3.6;
  const dashedDotR = manySeries ? 1.8 : 2.6;
  const dotStrokeW = manySeries ? 1.4 : 2.2;
  const dashedDotStrokeW = manySeries ? 1.2 : 1.8;
  const legendFontSize = manySeries ? 11 : 12;
  const legendPad = manySeries ? "3px 8px" : "4px 10px";
  const legendDotSize = manySeries ? 7 : 8;
  const legendGap = manySeries ? 5 : 6;
  return (
    <div>
      {/* <div style={{ display: "flex", gap: manySeries ? 6 : 8, marginBottom: 14, flexWrap: "wrap" }}>
        {series.map(s => (
          <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: legendGap, fontSize: legendFontSize, color: C.ink70, fontWeight: 600, background: C.linenDeep, padding: legendPad, borderRadius: 99 }}>
            <span style={{ width: legendDotSize, height: legendDotSize, borderRadius: s.dashed ? 2 : 99, background: s.color, display: "inline-block", flexShrink: 0 }} />{s.label}
          </span>
        ))}
      </div> */}
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {showArea && (
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={series[0].color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={series[0].color} stopOpacity="0" />
            </linearGradient>
          </defs>
        )}
        {tickVals.map((t, i) => (
          <g key={i}>
            <line x1={padL} x2={width - padR} y1={yFor(t)} y2={yFor(t)} stroke={C.lineSoft} strokeWidth="1" strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
            <text x={padL - 8} y={yFor(t) + 4} textAnchor="end" fontSize="10" fontFamily={FONT_MONO} fill={C.ink30}>{yFormatter(t)}</text>
          </g>
        ))}
        {categories.map((c, i) => (i % labelStep === 0 || i === categories.length - 1) && (
          <text key={c + i} x={xFor(i)} y={height - 8} textAnchor="middle" fontSize="10" fontFamily={FONT_BODY} fill={C.ink50}>{c}</text>
        ))}
        {showArea && (() => {
          const s = series[0];
          const line = s.values.map((v, i) => (i ? "L" : "M") + xFor(i).toFixed(1) + " " + yFor(v).toFixed(1)).join(" ");
          const areaD = `${line} L ${xFor(s.values.length - 1).toFixed(1)} ${yFor(minV).toFixed(1)} L ${xFor(0).toFixed(1)} ${yFor(minV).toFixed(1)} Z`;
          return <path d={areaD} fill={`url(#${gradId})`} stroke="none" />;
        })()}
        {series.map(s => {
          const d = s.values.map((v, i) => (i ? "L" : "M") + xFor(i).toFixed(1) + " " + yFor(v).toFixed(1)).join(" ");
          return (
            <g key={s.label}>
              <path d={d} fill="none" stroke={s.color} strokeWidth={s.dashed ? dashedLineW : lineW} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={s.dashed ? "6 5" : undefined} vectorEffect="non-scaling-stroke" />
              {s.values.map((v, i) => (!dense || i % labelStep === 0 || i === s.values.length - 1) && (
                <circle key={i} cx={xFor(i)} cy={yFor(v)} r={s.dashed ? dashedDotR : dotR} fill={C.card} stroke={s.color} strokeWidth={s.dashed ? dashedDotStrokeW : dotStrokeW} vectorEffect="non-scaling-stroke">
                  <title>{`${categories[i]}: ${yFormatter(v)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---- MANAGEMENT VIEW (exec overview) --------------------------------------
function HeroStat({ label, value, sub, deltaText, deltaPositive, progress, color }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 18, padding: "20px 24px", display: "flex", alignItems: "center", gap: 20, flex: "2 1 360px", minWidth: 280 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
        <div style={{ fontFamily: FONT_DISP, fontSize: 38, fontWeight: 700, color: C.ink, marginTop: 6, lineHeight: 1 }}>{value}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {deltaText && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 99, background: deltaPositive ? C.greenSoft : C.redSoft, color: deltaPositive ? C.green : C.red, fontSize: 12, fontWeight: 700 }}>{deltaPositive ? "▲" : "▼"} {deltaText}</span>}
          {sub && <span style={{ fontSize: 12.5, color: C.ink50 }}>{sub}</span>}
        </div>
      </div>
      <ProgressRing value={progress} color={color} size={92} stroke={9} />
    </div>
  );
}
function StatTile({ label, value, sub, accent }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: "12px 16px 12px 18px", flex: "1 1 150px", minWidth: 140, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: accent || C.indigo }} />
      <div style={{ fontSize: 10.5, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontFamily: FONT_DISP, fontSize: 19, fontWeight: 700, marginTop: 4, color: C.ink }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.ink50, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const GRANULARITY_LABEL = { day: "Daily points.", month: "Monthly points.", year: "Yearly points." };

function ManagementView({ rows, teams, period, managementData={}, chartData={}, chartRangeKey, onChartRangeChange }) {
  const [selectedUnitNames, setSelectedUnitNames] = useState(new Set()); // empty set = "all" (default)
  const [metric, setMetric] = useState("net");
  const [isAll, setIsAll] = useState(true);

  // const kpi = useMemo(() => {
  //   const s = { target: 0, gross: 0, net: 0, refunds: 0, onTarget: 0 };
  //   rows.forEach(r => { s.target += r.target; s.gross += r.gross; s.net += r.net; s.refunds += r.refunds; if (r.status === "on_target") s.onTarget++; });
  //   s.progress = s.target ? s.net / s.target : 0; s.refundRatio = s.gross ? s.refunds / s.gross : 0;
  //   return s;
  // }, [rows]);

  const kpi = managementData?.kpi

  const priorKey = PRIOR_PERIOD[period.key];
  const delta = useMemo(() => {
    if (!priorKey) return null;
    const priorPeriod = PERIODS.find(p => p.key === priorKey);
    const priorNet = AGENTS.map(a => computeRow(a, priorPeriod)).reduce((s, r) => s + r.net, 0);
    if (!priorNet) return null;
    return (kpi?.net - priorNet) / priorNet;
  }, [kpi?.net, priorKey]);
  const priorLabel = priorKey ? PERIODS.find(p => p.key === priorKey).label : null;

  const podRanked = [...teams].sort((a, b) => b.progress - a.progress);
  const topPerformers = [...rows].sort((a, b) => b.progress - a.progress).slice(0, 5);
  const needsAttention = [...rows].filter(r => r.status === "behind" || r.status === "at_risk").sort((a, b) => a.progress - b.progress).slice(0, 5);

  const chartUnits = (chartData.units || []).filter(u => u.name !== "Unassigned");
  const allUnitNames = useMemo(() => chartUnits.map(u => u.name), [chartUnits]);
  const unitRanking = (managementData?.unitRanking || []).filter(t => t.name !== "Unassigned");

  function toggleUnit(name) {
    setSelectedUnitNames(prev => {
      const base = prev.size ? prev : new Set(allUnitNames); // materialize the implicit "all" default on first interaction
      const n = new Set(base);
      if (n.has(name)) { if (n.size > 1) n.delete(name); } else n.add(name);
      return n;
    });
  }
  function selectAllUnits() { setSelectedUnitNames(new Set()); }

  // chart: date range is its own filter, driven by chartData.filter_options from the backend;
  // every selection shows a Target line (summed across whichever units are selected) plus one line per selected unit.
  const buckets = chartData.buckets || [];
  const filterOptions = chartData.filter_options || [];
  const rangeLabel = filterOptions.find(o => o.key === chartRangeKey)?.label || "";
  const metricLabel = { net: "Net", gross: "Gross" }[metric];
  const selectedUnits = selectedUnitNames.size ? chartUnits.filter(u => selectedUnitNames.has(u.name)) : chartUnits;
  const allCompany = chartData?.company ? [chartData?.company] : [];
  
  const series = useMemo(() => {
    const perUnit = selectedUnits.map(u => {
      const pointsByKey = {};
      (u.points || []).forEach(p => { pointsByKey[p.key] = p; });
      const values = buckets.map(b => pointsByKey[b.key]?.[metric] || 0);
      const targets = buckets.map(b => pointsByKey[b.key]?.target || 0);
      return { label: selectedUnits.length === 1 ? `${u.name} · ${metricLabel}` : u.name, color: colorForUnit(u.name), values, targets };
    });
    const targetValues = buckets.map((_, i) => perUnit.reduce((s, p) => s + p.targets[i], 0));
    const targetLabel = selectedUnits.length === chartUnits.length ? "Target (company)" : selectedUnits.length === 1 ? "Target" : "Target (selected units)";
    return [
      { label: targetLabel, color: C.ink30, dashed: true, values: targetValues },
      ...perUnit.map(({ label, color, values }) => ({ label, color, values }))
    ];
  }, [selectedUnits.map(u => u.name).join(","), buckets, metric]); // eslint-disable-line

  const companySeries = useMemo(() => {
    const perUnit = allCompany.map(u => {
      const pointsByKey = {};
      (u.points || []).forEach(p => { pointsByKey[p.key] = p; });
      const values = buckets.map(b => pointsByKey[b.key]?.[metric] || 0);
      const targets = buckets.map(b => pointsByKey[b.key]?.target || 0);
      return { label: "Company", color: C.indigo, values, targets };
    });
    const targetValues = buckets.map((_, i) => perUnit.reduce((s, p) => s + p.targets[i], 0));
    const targetLabel = allCompany.length === chartUnits.length ? "Target (company)" : allCompany.length === 1 ? "Target" : "Target (selected units)";
    return [
      { label: targetLabel, color: C.ink30, dashed: true, values: targetValues },
      ...perUnit.map(({ label, color, values }) => ({ label, color, values }))
    ];
  }, [buckets, metric]); // eslint-disable-line


  const chip = (active) => ({ padding: "7px 12px", borderRadius: 10, border: `1px solid ${active ? C.indigo : C.line}`, background: active ? C.indigoSoft : C.card, color: active ? C.indigo : C.ink50, fontSize: 12.5, fontWeight: 600, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 8 });
  const rangeChip = (active) => ({ padding: "6px 14px", borderRadius: 99, border: `1px solid ${active ? C.indigo : C.line}`, background: active ? C.indigoSoft : C.card, color: active ? C.indigo : C.ink50, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" });

  return (
    <div>
      {/* KPI cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "stretch" }}>
        <HeroStat label="Net revenue achieved" value={money(kpi?.net)} progress={kpi?.progress} color={C.indigo}
          deltaText={delta != null ? `${pct(Math.abs(delta))} vs ${priorLabel}` : null} deltaPositive={delta != null && delta >= 0}
          sub={`of ${money(kpi?.target)} target · ${period.label}`} />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: "3 1 480px" }}>
          <StatTile label="Gross achieved" value={money(kpi?.gross)} sub={`Refunded ${money(kpi?.refunds)}`} accent={C.ink70} />
          <StatTile label="Refund ratio" value={pct(kpi?.refundRatio)} sub="of gross revenue" accent={C.red} />
          <StatTile label="Pods on target" value={`${unitRanking.filter(t => t.status === "on_target").length} / ${unitRanking.length}`} sub="at 100%+" accent={C.green} />
          <StatTile label="Agents on target" value={`${kpi?.onTarget} / ${kpi?.agentCount}`} sub="at 100%+" accent={C.amber} />
        </div>
      </div>

      {/* pod ranking · top performers · needs attention */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, alignItems: "stretch" }}>
        {/* <div style={{ flex: "1 1 260px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", minWidth: 240 }}>
          <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 14 }}>Unit ranking · {period.label}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {unitRanking.map((t, i) => {
              const sm = STATUS_META[t.status] || { label: "Unassigned", c: C.gray, bg: C.graySoft}; const lead = i === 0; return (
                <div key={t.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: lead ? "8px 10px" : "0", borderRadius: 12, background: lead ? C.amberSoft : "transparent" }}>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, fontWeight: 700, color: lead ? C.amber : C.ink30, width: 16, textAlign: "center" }}>{i + 1}</span>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: colorForUnit(t.name), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{t.name}</div>
                    <ProgressBar value={t.progress} color={sm.c} />
                  </div>
                </div>
              );
            })}
          </div>
        </div> */}
        <MiniAgentTable title="Top performers" rows={managementData?.bestAgents || []} accent={C.green} />
        <MiniAgentTable title="Needs attention" rows={managementData?.needsAttention || []} accent={C.red} empty="No agents currently behind or at risk." />
      </div>

      {/* chart, full width, filters on the right */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px 0", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.ink30, textTransform: "uppercase", letterSpacing: 0.6 }}>Range</span>
          {filterOptions.map(o => (
            <button key={o.key} onClick={() => onChartRangeChange?.(o.key)} style={rangeChip(chartRangeKey === o.key)}>{o.label}</button>
          ))}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 460px", padding: "16px 20px 18px", minWidth: 280 }}>
            <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>
              {metricLabel} · {rangeLabel}
            </div>
            <TrendChart categories={buckets.map(b => b.label)} series={isAll ? companySeries : series} yFormatter={money} />
          </div>
          <div className="mgmt-side-panel" style={{ flex: "0 0 200px", borderLeft: `1px solid ${C.lineSoft}`, padding: "18px 16px", display: "flex", flexDirection: "column", gap: 16, minWidth: 180 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.ink30, textTransform: "uppercase", letterSpacing: 0.6 }}>Units</span>
                {/* <button onClick={selectAllUnits} style={{ background: "none", border: "none", color: C.indigo, fontSize: 11, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>All</button> */}
              </div>
              <div className="mgmt-filter-col" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button onClick={() => setIsAll(!isAll)} style={chip(isAll)}>
                  <span style={{ width: 8, height: 8, borderRadius: isAll ? 99 : 3, background: isAll ? C.indigo : C.line, flexShrink: 0, border: isAll ? "none" : `1px solid ${C.ink30}` }} />All Units
                </button>
                {chartUnits.map(u => {
                  const on = selectedUnitNames.size ? selectedUnitNames.has(u.name) : true; return (
                    <button key={u.name} onClick={isAll ? () => {} : () => toggleUnit(u.name)} style={{...chip(on && !isAll), cursor: isAll ? "not-allowed" : "pointer"}}>
                      <span style={{ width: 8, height: 8, borderRadius: on ? 99 : 3, background: on ? colorForUnit(u.name) : C.line, flexShrink: 0, border: on ? "none" : `1px solid ${C.ink30}` }} />{u.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.ink30, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Metric</div>
              <div className="mgmt-filter-col" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[["net", "Net vs Target"], ["gross", "Gross vs Target"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setMetric(k)} style={chip(metric === k)}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "10px 20px", borderTop: `1px solid ${C.lineSoft}`, fontSize: 11.5, color: C.ink50 }}>
          {selectedUnits.length === chartUnits.length ? "Showing all units against combined target." : selectedUnits.length === 1 ? `Showing ${selectedUnits[0].name} against its target.` : `Showing ${selectedUnits.length} units against their combined target.`} {GRANULARITY_LABEL[chartData.granularity] || ""}
        </div>
      </div>
    </div>
  );
}

function SkeletonBox({ width = "100%", height = 16, radius = 8, style }) {
  return <div className="skel" style={{ width, height, borderRadius: radius, ...style }} />;
}

function MiniAgentTableSkeleton({ title, accent, rowsCount = 5 }) {
  return (
    <div style={{ flex: "1 1 380px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden", minWidth: 320 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.lineSoft}`, fontSize: 12, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.6, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: accent || C.ink30, flexShrink: 0 }} />{title}
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {Array.from({ length: rowsCount }).map((_, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderTop: i ? `1px solid ${C.lineSoft}` : "none" }}>
            <SkeletonBox width={28} height={28} radius={8} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <SkeletonBox width={110} height={11} radius={4} style={{ marginBottom: 6 }} />
              <SkeletonBox width={150} height={10} radius={4} />
            </div>
            <SkeletonBox width={100} height={10} radius={4} />
            <SkeletonBox width={64} height={20} radius={99} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ManagementSkeleton() {
  return (
    <div>
      {/* KPI cards */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, alignItems: "stretch" }}>
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 18, padding: "20px 24px", display: "flex", alignItems: "center", gap: 20, flex: "2 1 360px", minWidth: 280 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SkeletonBox width={140} height={11} radius={4} style={{ marginBottom: 12 }} />
            <SkeletonBox width={160} height={30} radius={6} style={{ marginBottom: 12 }} />
            <SkeletonBox width={200} height={12} radius={4} />
          </div>
          <SkeletonBox width={92} height={92} radius="50%" />
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flex: "3 1 480px" }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: "12px 16px 12px 18px", flex: "1 1 150px", minWidth: 140 }}>
              <SkeletonBox width={70} height={9} radius={4} style={{ marginBottom: 10 }} />
              <SkeletonBox width={80} height={18} radius={6} style={{ marginBottom: 8 }} />
              <SkeletonBox width={60} height={10} radius={4} />
            </div>
          ))}
        </div>
      </div>

      {/* pod ranking · top performers · needs attention */}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, alignItems: "stretch" }}>
        <MiniAgentTableSkeleton title="Top performers" accent={C.green} />
        <MiniAgentTableSkeleton title="Needs attention" accent={C.red} />
      </div>

      {/* chart */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px 0", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {[0, 1, 2, 3, 4].map(i => (<SkeletonBox key={i} width={70} height={26} radius={99} />))}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 460px", padding: "16px 20px 18px", minWidth: 280 }}>
            <SkeletonBox width={160} height={11} radius={4} style={{ marginBottom: 16 }} />
            <SkeletonBox width="100%" height={280} radius={10} />
          </div>
          <div style={{ flex: "0 0 200px", borderLeft: `1px solid ${C.lineSoft}`, padding: "18px 16px", display: "flex", flexDirection: "column", gap: 16, minWidth: 180 }}>
            <div>
              <SkeletonBox width={50} height={10} radius={4} style={{ marginBottom: 10 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[0, 1, 2, 3].map(i => (<SkeletonBox key={i} width="100%" height={32} radius={10} />))}
              </div>
            </div>
            <div>
              <SkeletonBox width={54} height={10} radius={4} style={{ marginBottom: 10 }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {[0, 1].map(i => (<SkeletonBox key={i} width="100%" height={32} radius={10} />))}
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "10px 20px", borderTop: `1px solid ${C.lineSoft}` }}>
          <SkeletonBox width={280} height={11} radius={4} />
        </div>
      </div>
    </div>
  );
}

function MiniAgentTable({ title, rows, empty, accent }) {
  return (
    <div style={{ flex: "1 1 380px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden", minWidth: 320 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.lineSoft}`, fontSize: 12, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.6, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: accent || C.ink30, flexShrink: 0 }} />{title}
      </div>
      <div style={{ maxHeight: 380, overflow: "auto" }}>
        <table style={{ width: "100%" }}><tbody>
          {rows.map((r, i) => {
            const sm = STATUS_META[r.status] || { label: "Unassigned", c: C.gray, bg: C.graySoft}; return (
              <tr key={r.id} style={{ borderTop: i ? `1px solid ${C.lineSoft}` : "none" }}>
                <td style={{ padding: "9px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: sm.bg, color: sm.c, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{initials(r.name)}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: C.ink50 }}>{r.team} · {money(r.net)} net</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: "9px 16px", width: 130 }}><ProgressBar value={r.progress} color={sm.c} /></td>
                <td style={{ padding: "9px 16px", textAlign: "right", whiteSpace: "nowrap" }}><Pill label={sm.label} color={sm.c} bg={sm.bg} /></td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td style={{ padding: "28px 16px", textAlign: "center", color: C.ink50, fontSize: 12.5 }}>{empty || "No agents to show."}</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}

// ==========================================================================
export default function ManagementPage() {
  const apiCall = useApiCall();

  const [managementData, setManagementData] = useState({})
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(true);

  const [periodKey, setPeriodKey] = useState("this_month");
  const [pickerOpen, setPickerOpen] = useState(false);
  const period = PERIODS.find(p => p.key === periodKey);

  const rows = useMemo(() => AGENTS.map(a => { const r = computeRow(a, period); r.status = statusOf(r.progress); r.trend = trendOf(a.history); return r; }), [periodKey]);

  const teams = useMemo(() => {
    const map = {};
    TEAMS.forEach(t => map[t] = { name: t, agents: [], target: 0, gross: 0, net: 0, refunds: 0, onTarget: 0, histSum: [0, 0, 0, 0, 0, 0] });
    rows.forEach(r => {
      const t = map[r.team]; t.agents.push(r);
      t.target += r.target; t.gross += r.gross; t.net += r.net; t.refunds += r.refunds;
      if (r.status === "on_target") t.onTarget++;
      r.history.forEach((v, i) => t.histSum[i] += v);
    });
    return Object.values(map).map(t => {
      const n = t.agents.length;
      const history = t.histSum.map(v => v / n);
      const progress = t.target ? t.net / t.target : 0;
      const refundRatio = t.gross ? t.refunds / t.gross : 0;
      return { ...t, count: n, history, progress, refundRatio, status: statusOf(progress), trend: trendOf(history) };
    });
  }, [rows]);

  const selBar = { padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.line}`, background: C.card, fontFamily: FONT_BODY, fontSize: 13.5, color: C.ink, outline: "none", cursor: "pointer" };

  // ---- custom date range state ----
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [draftStart, setDraftStart] = useState("");
  const [draftEnd, setDraftEnd] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  function formatDateLabel(d) { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  const customRangeLabel = customStart && customEnd ? `${formatDateLabel(customStart)} – ${formatDateLabel(customEnd)}` : "Custom";
  const periodLabel = periodKey === "custom" ? customRangeLabel : period.label;

  function openPicker() {
    setPickerOpen(o => {
      const next = !o;
      if (next) { setShowCustomPicker(periodKey === "custom"); setDraftStart(customStart); setDraftEnd(customEnd); }
      return next;
    });
  }
  function selectPeriod(key) { setPeriodKey(key); setPickerOpen(false); setShowCustomPicker(false); }
  function openCustomPicker() { setShowCustomPicker(true); setDraftStart(customStart); setDraftEnd(customEnd); }
  function applyCustomRange() {
    if (!draftStart || !draftEnd || draftStart > draftEnd) return;
    setCustomStart(draftStart); setCustomEnd(draftEnd);
    setPeriodKey("custom"); setShowCustomPicker(false); setPickerOpen(false);
  }
  function cancelCustomRange() { setShowCustomPicker(false); }


  const getManagementSummary = async () => {
    if(periodKey==="custom"&&(!customStart||!customEnd))return;
    setIsSummaryLoading(true);
    try {
      const rangeQs=periodKey==="custom"?`&from=${customStart}&to=${customEnd}`:"";
      const apiRes = await apiCall({ method: "get", url: `management/summary?period=${periodKey}${rangeQs}` })
      setManagementData(apiRes.data);
    } catch (error) {
      console.log("Errr", error)
    } finally {
      setIsSummaryLoading(false);
    }
  }

  useEffect(() => {
    getManagementSummary();
  }, [periodKey,customStart,customEnd])

  const [chartData, setChartData] = useState({})
  const [chartRangeKey, setChartRangeKey] = useState("this_year")

  const getChart = async() => {
    setIsChartLoading(true);
    try {
      const apiRes = await apiCall({method: "get", url: `management/summary-charts?period=${chartRangeKey}`})
      // console.log("apiRes", apiRes);
      setChartData(apiRes.data)
    } catch (error) {
      console.log("Err", error)
    } finally {
      setIsChartLoading(false);
    }
  }

  useEffect(() => {
    getChart()
  }, [chartRangeKey])

  return (
    <div style={{ minHeight: "100vh", background: C.linen, fontFamily: FONT_BODY, color: C.ink, padding: "24px 28px 40px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box}table{border-collapse:collapse}
        ::-webkit-scrollbar{height:9px;width:9px}::-webkit-scrollbar-thumb{background:#D8C7AF;border-radius:9px}
        @keyframes skeletonPulse{0%,100%{opacity:.55}50%{opacity:1}}
        .skel{background:${C.lineSoft};animation:skeletonPulse 1.4s ease-in-out infinite}
        @media (max-width: 820px){
          .mgmt-side-panel{flex:1 1 100% !important;border-left:none !important;border-top:1px solid ${C.lineSoft}}
          .mgmt-filter-col{flex-direction:row !important;flex-wrap:wrap}
        }
      `}</style>

      <div style={{ maxWidth:1240, margin: "0 auto" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.indigo, textTransform: "uppercase", letterSpacing: 1.4 }}>Legendesk · Sales</div>
            <h1 style={{ fontFamily: FONT_DISP, fontSize: 30, fontWeight: 700, margin: "4px 0 0", letterSpacing: -0.5 }}>Management Overview</h1>
          </div>
          {/* <div style={{ position: "relative" }}>
            <button onClick={() => setPickerOpen(o => !o)} style={{ ...selBar, display: "inline-flex", alignItems: "center", gap: 10, fontWeight: 600 }}>
              <span style={{ width: 7, height: 7, borderRadius: 99, background: C.indigo }} />{period.label}<span style={{ color: C.ink30, fontSize: 11 }}>▼</span>
            </button>
            {pickerOpen && (
              <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: 6, boxShadow: "0 12px 30px rgba(10,12,18,.12)", zIndex: 20, width: 190 }}>
                {PERIODS.map(p => (<button key={p.key} onClick={() => { setPeriodKey(p.key); setPickerOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: p.key === periodKey ? 600 : 400, color: p.key === periodKey ? C.indigo : C.ink70, background: p.key === periodKey ? C.indigoSoft : "transparent" }}>{p.label}</button>))}
              </div>
            )}
          </div> */}
          <div style={{position:"relative"}}>
            <button onClick={openPicker} style={{...selBar,display:"inline-flex",alignItems:"center",gap:10,fontWeight:600}}>
              <span style={{width:7,height:7,borderRadius:99,background:C.indigo}}/>{periodLabel}<span style={{color:C.ink30,fontSize:11}}>▼</span>
            </button>
            {pickerOpen&&(
              <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:C.card,border:`1px solid ${C.line}`,borderRadius:12,padding:6,boxShadow:"0 12px 30px rgba(10,12,18,.12)",zIndex:20,width:220}}>
                {PERIODS.filter(p=>p.key!=="custom").map(p=>(<button key={p.key} onClick={()=>selectPeriod(p.key)} style={{display:"block",width:"100%",textAlign:"left",padding:"8px 10px",borderRadius:8,border:"none",cursor:"pointer",fontFamily:FONT_BODY,fontSize:13.5,fontWeight:p.key===periodKey?600:400,color:p.key===periodKey?C.indigo:C.ink70,background:p.key===periodKey?C.indigoSoft:"transparent"}}>{p.label}</button>))}
                <button onClick={openCustomPicker} style={{display:"block",width:"100%",textAlign:"left",padding:"8px 10px",borderRadius:8,border:"none",cursor:"pointer",fontFamily:FONT_BODY,fontSize:13.5,fontWeight:periodKey==="custom"?600:400,color:periodKey==="custom"?C.indigo:C.ink70,background:periodKey==="custom"?C.indigoSoft:"transparent"}}>
                  {periodKey==="custom"?customRangeLabel:"Custom…"}
                </button>
                {showCustomPicker&&(
                  <div style={{padding:10,borderTop:`1px solid ${C.lineSoft}`,marginTop:4,display:"flex",flexDirection:"column",gap:8}}>
                    <label style={{fontSize:11.5,color:C.ink50,fontWeight:600}}>Start date
                      <input type="date" value={draftStart} max={draftEnd||undefined} onChange={e=>setDraftStart(e.target.value)} style={{display:"block",width:"100%",marginTop:4,padding:"6px 8px",borderRadius:8,border:`1px solid ${C.line}`,fontFamily:FONT_BODY,fontSize:13}}/>
                    </label>
                    <label style={{fontSize:11.5,color:C.ink50,fontWeight:600}}>End date
                      <input type="date" value={draftEnd} min={draftStart||undefined} onChange={e=>setDraftEnd(e.target.value)} style={{display:"block",width:"100%",marginTop:4,padding:"6px 8px",borderRadius:8,border:`1px solid ${C.line}`,fontFamily:FONT_BODY,fontSize:13}}/>
                    </label>
                    <div style={{display:"flex",gap:6,marginTop:2}}>
                      <button onClick={applyCustomRange} disabled={!draftStart||!draftEnd||draftStart>draftEnd} style={{flex:1,padding:"7px 10px",borderRadius:8,border:"none",cursor:(!draftStart||!draftEnd||draftStart>draftEnd)?"not-allowed":"pointer",background:C.indigo,color:"#fff",fontSize:12.5,fontWeight:600,opacity:(!draftStart||!draftEnd||draftStart>draftEnd)?0.5:1}}>Apply</button>
                      <button onClick={cancelCustomRange} style={{flex:1,padding:"7px 10px",borderRadius:8,border:`1px solid ${C.line}`,cursor:"pointer",background:C.card,color:C.ink70,fontSize:12.5,fontWeight:600}}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {(isSummaryLoading || isChartLoading)
          ? <ManagementSkeleton/>
          : <ManagementView rows={rows} teams={teams} period={period} managementData={managementData} chartData={chartData} chartRangeKey={chartRangeKey} onChartRangeChange={setChartRangeKey} />}
      </div>
    </div>
  );
}
